import { useState } from 'react';
import { Link, ui } from '../ui';
import { AuthScreen, Field, Notice } from './shared';
import { authClient, useSession } from '../auth';

/**
 * Forgetting a password, and setting a new one.
 *
 * Two screens rather than one, because they are separated by an email and by
 * however long somebody takes to read it. The first proves nothing and asks for
 * an address; the second holds a token out of that email and is the only place
 * a password is typed.
 *
 * **This flow did not exist.** The hosted provider owned it, and when identity
 * moved in-house the sign-in page came with it but this did not — which left a
 * forgotten password as an unrecoverable account, silently, with no error
 * anywhere to notice. Self-hosting identity means owning the unhappy paths too.
 */

/** Where the emailed link comes back to. Absolute: it travels out of the app. */
function resetTarget(): string {
  return `${window.location.origin}/reset-password`;
}

export function ForgotPasswordRoute(): React.ReactElement {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const email = String(new FormData(event.currentTarget).get('email') ?? '');

    setBusy(true);
    await authClient.requestPasswordReset({ email, redirectTo: resetTarget() });

    /**
     * The same screen either way, and the result is deliberately not read.
     *
     * Better Auth answers this identically whether or not the address has an
     * account, so that the endpoint cannot be used to find out who has one.
     * Branching on the response here would hand that back — and so would
     * showing an error, because "something went wrong" for one address and
     * "check your email" for another is the same oracle wearing a different
     * hat.
     */
    setSent(true);
    setBusy(false);
  }

  if (sent) {
    return (
      <AuthScreen
        title="Check your email"
        foot={
          <p>
            Nothing arrived? Check spam, then <Link href="/forgot-password">try again</Link>.
          </p>
        }
      >
        <p className="gb-body">
          If that address has an account, a link to set a new password is on its way. It runs out in
          an hour.
        </p>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen
      title="Reset your password"
      foot={
        <p>
          Remembered it? <Link href="/sign-in">Sign in</Link>.
        </p>
      }
    >
      <form className={ui.stack ?? ''} onSubmit={(event) => void submit(event)}>
        <p className="gb-body">
          Type the address you signed up with. We will send you a link to set a new password.
        </p>

        <Field label="Email">
          <input
            className={ui.input ?? ''}
            name="email"
            type="email"
            required
            autoComplete="email"
            autoFocus
          />
        </Field>

        <button type="submit" className={ui.button ?? ''} disabled={busy}>
          {busy ? 'One moment…' : 'Send the link'}
        </button>
      </form>
    </AuthScreen>
  );
}

export function ResetPasswordRoute(): React.ReactElement {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const token = new URLSearchParams(window.location.search).get('token');

  /**
   * No token, no form.
   *
   * Rendering the fields and failing on submit would ask somebody to choose and
   * type a password twice before telling them the link was the problem.
   */
  if (!token) {
    return (
      <AuthScreen title="That link has expired">
        <p className="gb-body">
          Reset links last an hour and work once.{' '}
          <Link href="/forgot-password">Ask for a new one</Link>.
        </p>
      </AuthScreen>
    );
  }

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get('password') ?? '');

    if (password !== String(form.get('confirm') ?? '')) {
      setError('Those two passwords are not the same.');
      return;
    }

    setBusy(true);
    setError(null);

    const result = await authClient.resetPassword({
      newPassword: password,
      token: token as string,
    });

    if (result.error) {
      // Usually an expired or already-used token, which is worth saying plainly
      // rather than as a generic failure — the fix is a new link, not a retry.
      setError(result.error.message ?? 'That link is no longer valid. Ask for a new one.');
      setBusy(false);
      return;
    }

    // Resetting does not sign anybody in, so the honest next step is sign-in
    // with the password they just chose.
    window.location.href = '/sign-in';
  }

  return (
    <AuthScreen title="Set a new password">
      <form className={ui.stack ?? ''} onSubmit={(event) => void submit(event)}>
        {error ? <Notice alert>{error}</Notice> : null}

        <Field label="New password">
          <input
            className={ui.input ?? ''}
            name="password"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            autoFocus
          />
        </Field>

        <Field label="Again">
          <input
            className={ui.input ?? ''}
            name="confirm"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
          />
        </Field>

        <button type="submit" className={ui.button ?? ''} disabled={busy}>
          {busy ? 'One moment…' : 'Save it'}
        </button>
      </form>
    </AuthScreen>
  );
}

/**
 * Where the confirmation link lands.
 *
 * A receipt rather than a step: it says what the click achieved and gets out of
 * the way.
 *
 * **It no longer claims to have signed anybody in.** `autoSignInAfterVerification`
 * does create a session, and on a *redirect* the bearer token it comes back on
 * is a response header the browser never exposes to script — the same fact that
 * makes social sign-in a popup rather than a redirect. So in the browser that
 * signed up, what lifts the wall is the session that was already there, now
 * reading as confirmed; and in a browser that only opened the link — the phone
 * somebody reads their email on — nothing was signed in at all. One sentence
 * that is true in both is better than a warmer one that is false in the second.
 */
export function VerifiedRoute(): React.ReactElement {
  return (
    <AuthScreen
      title="Address confirmed"
      foot={
        <p>
          <Link href="/library">Go to your library</Link>.
        </p>
      }
    >
      <p className="gb-body">
        Thanks — that address is yours and your account is open. If you started signing up in
        another window, go back to it.
      </p>
    </AuthScreen>
  );
}

/**
 * The wall: signed in, and nothing works until the address is confirmed.
 *
 * Two ways here, one screen. It is the route at `/verify-email`, which is where
 * signing up with an address ends; and it is what `Shell` renders in place of
 * any page behind the session gate while `emailVerified` is false — so it also
 * catches somebody who signed in weeks later, and somebody who signed up before
 * this rule existed.
 *
 * **The API is the gate, not this screen.** `requireCaller` refuses every route
 * bar two for an unconfirmed caller, so what this does is explain a refusal
 * that has already happened rather than be the thing preventing it. A wall
 * drawn only in the browser would be a wall with a `fetch` through it.
 *
 * ## The three things somebody stuck here needs
 *
 * **The address**, because the commonest reason nothing arrived is that it went
 * to a typo. It is read off the session rather than asked for.
 *
 * **A way to send it again**, which is `sendVerificationEmail` — the same
 * endpoint, the same template, the same callback. It answers 200 whatever
 * happens, so this reports what it asked for rather than what it achieved.
 *
 * **A way out**, which is a link to sign in as somebody else. Deliberately not
 * a sign-out button: `endSession` is called from the account page and nowhere
 * else — a rule `apps/app/test/account.test.ts` pins — and signing in again
 * replaces the stored token anyway. The session left behind can do nothing at
 * all, which is what makes leaving it there safe rather than untidy.
 *
 * **And a way forward once it is done.** Somebody who confirms in a second tab
 * has no way to tell this one: `useSession` asked once, on load. So the button
 * is an honest reload rather than a promise — it re-asks, and the wall lifts if
 * the answer changed.
 */
export function VerifyEmailRoute(): React.ReactElement {
  const { data: session } = useSession();
  const address = session?.user.email ?? null;

  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function resend(): Promise<void> {
    if (!address) return;

    setBusy(true);
    /*
      The result is deliberately not read, exactly as the password reset above
      does not read its own. This endpoint answers the same way whether or not
      there was anything to send, and branching on it here would only invent a
      difference for somebody to worry about.
    */
    await authClient.sendVerificationEmail({
      email: address,
      // Absolute: the API builds the link, and a relative path would resolve
      // against its origin rather than this one.
      callbackURL: `${window.location.origin}/verified`,
    });

    setSent(true);
    setBusy(false);
  }

  return (
    <AuthScreen
      title="Confirm your email"
      foot={
        <>
          <p>Nothing arrived? Check spam. Each link lasts an hour.</p>
          <p>
            Wrong address? <Link href="/sign-in">Sign in as someone else</Link>.
          </p>
        </>
      }
    >
      <p className="gb-body">
        {address ? (
          <>
            We sent a link to <strong>{address}</strong>. Click it and your account opens. Until
            then there is nothing to do here.
          </>
        ) : (
          <>Sign in again and we will send you a fresh link.</>
        )}
      </p>

      {sent ? <Notice>Sent. Give it a minute.</Notice> : null}

      <div className={ui.stack ?? ''}>
        <button type="button" className={ui.button ?? ''} onClick={() => window.location.reload()}>
          I have confirmed it
        </button>

        {address ? (
          <button
            type="button"
            className={ui.buttonSecondary ?? ''}
            disabled={busy}
            onClick={() => void resend()}
          >
            {busy ? 'One moment…' : 'Send it again'}
          </button>
        ) : null}
      </div>
    </AuthScreen>
  );
}
